import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, TextField } from '@mui/material';
import ObjectID from 'bson-objectid';
import ButtonCustom from 'components/ButtonCustom';
import ImageDropZone from 'components/ImageDropZone';
import Restrict from 'components/Restrict';
import UseAuthContext from 'context/AuthContext';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { addOrganization, updateBusinessSetting } from 'services/business-setting-service';
import { USER_ROLES } from 'utils/constants';
import { IOrganizationUpdate } from 'utils/contracts';
import { getErrorHtmlContent } from 'utils/utils';

const BusinessSetting = ({ mode, organizationCallback, dataOrganization }: any) => {
  const { role, setOrganization } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [preview, setPreview] = useState(dataOrganization?.image_url ?? null);
  const [image_url, setImageUrl] = useState(dataOrganization?.image_url ?? null);
  const [image_error, setImageError] = useState('');
  const [name, setName] = useState(dataOrganization?.company_name ?? '');
  const [address, setAddress] = useState(dataOrganization?.company_address ?? '');
  const [isInspectionType, setIsInspectionType] = useState(dataOrganization?.isInspectionType ?? false);
  const [isLoading, setisLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  
  useEffect(() => {
    if (dataOrganization && mode !== 'create') {
      setName(dataOrganization?.company_name || '');
      setAddress(dataOrganization?.company_address || '');
      setImageUrl(dataOrganization?.image_url ?? null);
      setIsInspectionType(dataOrganization?.isInspectionType ?? false);
    }
  }, [dataOrganization, mode]);

  const readOnly = useMemo(() => {
    return role &&
      role !== USER_ROLES.ADMIN &&
      ![USER_ROLES.MANAGER].includes(role)
      ? true
      : false;
  }, [role]);

  const onImageDrop = (files: any, maxSize: any) => {
    const file = files[0];
    let reader = new FileReader();

    reader.onloadend = (e: any) => {
      setPreview(e.target.result);
      setImageUrl(null);
    };

    if (file) {
      if (file.size > maxSize) {
        setImageError(t('IMAGE_SIZE_ERROR'));
        return;
      }
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (e: any) => {
    e.preventDefault();
    setPreview(null);
    setImageUrl(null);
    e.stopPropagation();
  };

  const generateMongoId = () => {
    const newObjectId = new ObjectID();
    return newObjectId.toString();
  };

  const onClickHandler = async (e: any) => {
    e.preventDefault();
    try {
      setisLoading(true);
      let response;
      if (mode === 'create') {
        const id =generateMongoId();
        response = await addOrganization({
          image_url: image_url ?? '',
          company_name: name,
          company_address: address,
          preview: preview,
          id: id,
          isInspectionType: isInspectionType,
        });
      } else {
        response = await updateBusinessSetting({
          image_url: image_url || undefined,
          company_name: name,
          id: dataOrganization?.id,
          company_address: address,
          preview: preview,
          isInspectionType: isInspectionType,
        } as IOrganizationUpdate);
      }

      if (response.status === 200) {
        queryClient.invalidateQueries(['getOrganizationById']);
        setOrganization(response.data);
        enqueueSnackbar(t('BUSINESS_SETTING_UPDATE_SUCCESS'), {
          variant: 'success',
        });
        if (mode === 'create') {
          organizationCallback(response.data.id);

        }
        
        setisLoading(false);

      }
    } catch (error: any) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.log('onError', error);
      }
      setisLoading(false);
    }
  };


  return (
    <Box sx={{ display: "flex", gap: "30px", flexDirection: "column", justifyContent: "flex-start" }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          flexWrap: "wrap",
          gap: {sm: "30px", xs: "30px"},
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          width: { xs: "100%", sm: "480px" },
        }}
          className="tab-content-form"
        >
          <TextField
            fullWidth
            variant='standard'
            id='name'
            name='name'
            type='text'
            autoFocus
            label={t('EDIT_BUSINESS_SETTING_NAME')}
            autoComplete='off'
            autoCorrect='off'
            helperText={t('EDIT_BUSINESS_SETTING_NAME_HELPER_TEXT')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
          />
          <TextField
            fullWidth
            variant='standard'
            id='address'
            name='address'
            type='text'
            label={t('EDIT_BUSINESS_SETTING_ADDRESS')}
            autoComplete='off'
            autoCorrect='off'
            helperText={t('EDIT_BUSINESS_SETTING_ADDRESS_HELPER_TEXT')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={readOnly}
          />
          {role ===USER_ROLES.ADMIN && <FormControlLabel
              control={
                <Checkbox
                  checked={isInspectionType}
                  onChange={(e) => setIsInspectionType(e.target.checked)}
                  disabled={readOnly}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Box component="span" >
                    {t('EDIT_BUSINESS_SETTING_INSPECTION_TYPE')}
                  </Box>
                 
                </Box>
              }
            />}
        </Box>
        <Box sx={{ display: "flex", width: {sm:"62%", md:"50%"}, justifyContent:{sm:'flex-start', md:'flex-start'} }}>
          <ImageDropZone
            isLoading={isLoading}
            onImageDrop={onImageDrop}
            preview={preview}
            image_url={image_url}
            onImageRemove={removeImage}
            enabled={!readOnly}
            size={500000}
            image_error={image_error}
            customStyle={{ height: '100%', width: '170px', margin: "0px" }}
          />
        </Box>
      </Box>
      <Restrict forbidden={['viewer', 'member', 'inspector', 'inspector_manager']}>
        <Grid textAlign={"justify"} display={"flex"} justifyContent={{sm:"flex-start", md:"flex-start"}}>
          <ButtonCustom
            color='primary'
            type='submit'
            variant='contained'
            onClick={onClickHandler}
            disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {t('SAVE_BUTTON')}
          </ButtonCustom>
        </Grid>
      </Restrict>
    </Box>
  );
};

export default BusinessSetting;